import { useMutation } from '@apollo/client/react';
import { VERIFY_EMAIL_MUTATION } from '../../integrations/allkort/graphql/gql/EmailMutations';
import { VerifyEmailResponse } from '../../integrations/allkort/graphql/interface/Emails';

/**
 * useVerifyEmail
 *
 * Custom hook for verifying an email address using the code sent to the inbox.
 *
 * @example
 * ```tsx
 * const { verifyEmail, loading, error } = useVerifyEmail();
 *
 * const handleVerify = async () => {
 *   const result = await verifyEmail({ emailId: 382332, verificationCode: '2889' });
 *   if (result) console.log('Verified:', result);
 * };
 * ```
 */
export const useVerifyEmail = () => {
	const [verifyEmailMutation, { loading, error, data }] = useMutation<VerifyEmailResponse>(
		VERIFY_EMAIL_MUTATION
	);

	/**
	 * Verify an email address with the code sent to the inbox
	 * @param emailId           - The ID of the unverified email (returned from addEmails)
	 * @param verificationCode  - The 4-digit code sent to the email address
	 * @returns The verified email list, or null if the mutation failed
	 * @throws Error with a user-friendly message if the code is incorrect
	 */
	const verifyEmail = async ({
		emailId,
		verificationCode,
	}: {
		emailId: number
		verificationCode: string
	}) => {
		try {
			const result = await verifyEmailMutation({
				variables: { emailId, verificationCode },
			});

			if (result.error) {
				const message = (result.error as any).graphQLErrors?.[0]?.message ?? result.error.message;
				console.error('[useVerifyEmail] GraphQL error:', message);
				throw new Error(message);
			}

			console.log('[useVerifyEmail] Verification successful:', result.data?.verifyEmail?.email);
			return result.data?.verifyEmail?.email ?? null;
		} catch (err) {
			// Re-throw so the caller can inspect the message
			throw err;
		}
	};

	return { verifyEmail, loading, error, data };
};
